import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Keeps the header cart icon synchronized with Shopify's actual cart.
 *
 * This component supports both Ritual's native CartLinesUpdateEvent and the
 * custom cart:updated event emitted by custom Ajax add-to-cart components.
 * The server-rendered cart count is also revalidated on every page load so
 * stale sessionStorage can never hide a real cart item.
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.addEventListener('cart:updated', this.onLegacyCartUpdate);
    document.addEventListener('cart:change', this.onLegacyCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // The server-rendered count is authoritative after navigation/reload.
    // Re-fetch it so the icon cannot be left stale by sessionStorage.
    this.refreshFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener('cart:updated', this.onLegacyCartUpdate);
    document.removeEventListener('cart:change', this.onLegacyCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) this.refreshFromCart();
  };

  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? cart?.item_count ?? detail?.itemCount ?? 0;
        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  onLegacyCartUpdate = (event) => {
    const cart = event?.detail?.cart;
    if (cart) {
      const itemCount = cart.totalQuantity ?? cart.item_count ?? 0;
      this.renderCartBubble(itemCount);
      return;
    }

    this.refreshFromCart();
  };

  refreshFromCart = async () => {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);

      const cart = await response.json();
      this.renderCartBubble(cart.item_count ?? 0, false);
    } catch (error) {
      console.warn('[cart-icon] Unable to refresh cart:', error);
    }
  };

  renderCartBubble = async (itemCount, animate = true) => {
    const count = Number(itemCount) || 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);
    this.currentCartCount = count;
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    // Keep a short-lived value only as a visual cache; it is never used as the
    // source of truth during page initialization.
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(this.currentCartCount), timestamp: Date.now() })
    );

    if (!animate || count === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
